import { Component, type ErrorInfo, type ReactNode } from "react";

import { translateMessage } from "@/i18n";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export const UI_ERROR_EVENT = "celsus:ui-error";

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  public state: AppErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Deliberately exclude error messages, component stacks, URLs, and user data.
    window.dispatchEvent(
      new CustomEvent(UI_ERROR_EVENT, {
        detail: { errorName: error.name },
      })
    );
    console.error("ui_render_error", error.name, errorInfo);
  }

  public render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas px-5">
        <section
          aria-labelledby="application-error-title"
          className="max-w-lg rounded-card border border-line bg-surface p-8 text-center shadow-card"
          role="alert"
        >
          <h1 className="font-display text-3xl text-ink" id="application-error-title">
            {translateMessage("common.AppErrorBoundary.title")}
          </h1>
          <p className="mt-3 text-muted">
            {translateMessage("common.AppErrorBoundary.description")}
          </p>
          <button
            className="mt-6 min-h-11 rounded-md bg-ink px-5 text-sm font-semibold text-surface"
            onClick={() => window.location.reload()}
            type="button"
          >
            {translateMessage("common.AppErrorBoundary.reload")}
          </button>
        </section>
      </main>
    );
  }
}
